/**
 * Click-into-card detail. Works for any card type (idea, session, cluster).
 * Jira-style: a focused view of the card with threaded comments.
 */

import { useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { Modal } from './Modal';
import { Avatar } from './Avatar';
import { UiNode } from '../lib/UiNode';
import { IconSend, IconTrash } from './Icon';
import { MentionTextarea } from './MentionTextarea';
import { CommentRender } from './CommentRender';
import type { BoardDetail } from '../../shared/types';

type SubjectKind = 'idea' | 'session' | 'cluster';

export interface CardComment {
  id: string;
  authorId: string;
  authorName: string;
  authorHue: number;
  parentId: string | null;
  body: string;
  emoji: string | null;
  createdAt: number;
  updatedAt: number;
}

export function CardDetail({
  open,
  onClose,
  board,
  kind,
  subjectId,
  header,
  body,
}: {
  open: boolean;
  onClose: () => void;
  board: BoardDetail;
  kind: SubjectKind;
  subjectId: string;
  header: React.ReactNode;
  body?: React.ReactNode;
}) {
  const [comments, setComments] = useState<CardComment[] | null>(null);
  const [input, setInput] = useState('');
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const me = board.role;
  void me;

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const load = () =>
      void api
        .listComments(board.id, kind, subjectId)
        .then((r) => {
          if (!cancelled) setComments(r.comments);
        })
        .catch((e) => setError(e instanceof Error ? e.message : 'Failed'));
    load();

    // Refetch when another client adds/deletes a comment on this
    // subject. BoardView's WebSocket handler dispatches this event
    // for any activity of kind comment.added / comment.deleted.
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent).detail as
        | { subjectKind?: string; subjectId?: string }
        | undefined;
      if (!detail) return;
      if (detail.subjectKind === kind && detail.subjectId === subjectId) {
        load();
      }
    };
    window.addEventListener('synfolio:comment-changed', onChange);
    return () => {
      cancelled = true;
      window.removeEventListener('synfolio:comment-changed', onChange);
    };
  }, [open, board.id, kind, subjectId]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [comments]);

  async function send() {
    const text = input.trim();
    if (!text || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.createComment(board.id, kind, subjectId, {
        body: text,
        parentId: replyTo,
      });
      setComments((cur) => [...(cur ?? []), r.comment]);
      setInput('');
      setReplyTo(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    if (!confirm('Delete this comment?')) return;
    try {
      await api.deleteComment(board.id, id);
      setComments((cur) => (cur ?? []).filter((c) => c.id !== id && c.parentId !== id));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
    }
  }

  // Build tree: root comments, then children grouped by parentId
  const tree = buildTree(comments ?? []);

  return (
    <Modal open={open} onClose={onClose} title={header} maxWidth={620}>
      <div className="flex max-h-[70vh] flex-col gap-3">
        {body && <div className="flex-shrink-0 text-sm text-cream-2">{body}</div>}

        <header className="flex-shrink-0 border-b border-ink-rim/60 pb-2 text-xs uppercase tracking-wide text-cream-3">
          Discussion · {comments?.length ?? 0} message{(comments?.length ?? 0) === 1 ? '' : 's'}
        </header>

        <div ref={scrollerRef} className="flex flex-1 flex-col gap-3 overflow-y-auto">
          {comments && comments.length === 0 && (
            <p className="my-6 text-center text-xs text-cream-3">
              Nothing here yet — start the discussion.
            </p>
          )}
          {tree.roots.map((c) => (
            <CommentNode
              key={c.id}
              comment={c}
              members={board.members}
              children={tree.byParent.get(c.id) ?? []}
              onReply={(id) => setReplyTo(id)}
              onDelete={remove}
              replyTo={replyTo}
            />
          ))}
        </div>

        {replyTo && (
          <div className="flex items-center gap-2 rounded-md bg-ink-3 px-2 py-1 text-xs">
            <span className="text-cream-3">Replying to comment</span>
            <button type="button" className="ml-auto text-cream-3" onClick={() => setReplyTo(null)}>
              Cancel
            </button>
          </div>
        )}

        <form
          className="flex flex-shrink-0 items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void send();
          }}
        >
          <div className="min-w-0 flex-1">
            <MentionTextarea
              value={input}
              onChange={setInput}
              onSubmit={() => void send()}
              members={board.members}
              disabled={busy}
              placeholder={replyTo ? 'Write a reply… (@ to tag)' : 'Add a comment… (@ to tag or pick a date)'}
            />
          </div>
          <UiNode
            as="button"
            name="card-comment-send"
            entity="comment"
            action="send"
            state={busy ? 'busy' : 'idle'}
            type="submit"
            className="btn btn-primary inline-flex items-center gap-1"
            disabled={busy || !input.trim()}
          >
            <IconSend size={14} />
            <span className="hidden sm:inline">Send</span>
          </UiNode>
        </form>
        {error && <p className="px-1 text-xs text-rust">{error}</p>}
      </div>
    </Modal>
  );
}

function buildTree(list: CardComment[]) {
  const roots: CardComment[] = [];
  const byParent = new Map<string, CardComment[]>();
  const byId = new Map(list.map((c) => [c.id, c]));
  for (const c of list) {
    if (c.parentId && byId.has(c.parentId)) {
      const arr = byParent.get(c.parentId) ?? [];
      arr.push(c);
      byParent.set(c.parentId, arr);
    } else {
      roots.push(c);
    }
  }
  return { roots, byParent };
}

function CommentNode({
  comment,
  members,
  children,
  onReply,
  onDelete,
  replyTo,
}: {
  comment: CardComment;
  members: import('../../shared/comment-body').MemberRef[];
  children: CardComment[];
  onReply: (id: string) => void;
  onDelete: (id: string) => void;
  replyTo: string | null;
}) {
  void replyTo;
  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-2">
        <Avatar
          user={{ id: comment.authorId, name: comment.authorName, avatarHue: comment.authorHue, avatarUrl: null }}
          size={26}
        />
        <div className="flex-1 rounded-2xl rounded-tl-sm bg-ink-2 p-3">
          <div className="mb-1 flex items-baseline gap-2 text-xs">
            <span className="font-medium">{comment.authorName}</span>
            <span className="text-cream-3">{relativeTime(comment.createdAt)}</span>
          </div>
          <div className="whitespace-pre-wrap break-words text-sm">
            {comment.emoji && <span className="mr-1">{comment.emoji}</span>}
            <CommentRender body={comment.body} members={members} />
          </div>
          <div className="mt-1.5 flex items-center gap-1">
            <button
              type="button"
              className="text-[11px] text-cream-3 hover:text-cream-2"
              onClick={() => onReply(comment.id)}
            >
              Reply
            </button>
            <button
              type="button"
              className="ml-auto text-cream-3 hover:text-rust"
              onClick={() => onDelete(comment.id)}
              title="Delete"
            >
              <IconTrash size={12} />
            </button>
          </div>
        </div>
      </div>
      {children.length > 0 && (
        <div className="ml-7 flex flex-col gap-2 border-l-2 border-ink-rim/60 pl-3">
          {children.map((c) => (
            <CommentNode
              key={c.id}
              comment={c}
              members={members}
              children={[]}
              onReply={onReply}
              onDelete={onDelete}
              replyTo={replyTo}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function relativeTime(unixSec: number): string {
  const now = Math.floor(Date.now() / 1000);
  const d = now - unixSec;
  if (d < 60) return 'just now';
  if (d < 3600) return `${Math.floor(d / 60)}m`;
  if (d < 86400) return `${Math.floor(d / 3600)}h`;
  return `${Math.floor(d / 86400)}d`;
}
